import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { INTENT } from "../lib/helpers";

// Attachement d'un formulaire sur une phase (admin) — miroir de RdvEditor :
// bouton "+ Attacher" replié par défaut, choix modèle existant ou personnalisé
// une fois ouvert, bouton "Réinitialiser" une fois le formulaire complété.
export const FormEditor = ({ form, onSave, onReset, testId = "phase" }) => {
  const hasForm = !!form;
  const [open, setOpen] = useState(hasForm);
  const [mode, setMode] = useState("template"); // "template" | "custom"
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (open && mode === "template" && !hasForm) {
      api.get("/admin/form-templates").then((r) => setTemplates(r.data.filter((t) => t.status === "active"))).catch(() => {});
    }
  }, [open, mode, hasForm]);

  const attachTemplate = async () => {
    if (!templateId) return toast.error("Choisissez un modèle");
    setSaving(true);
    try {
      await onSave({ update_form: true, form_template_id: templateId });
      toast.success("Formulaire attaché");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'attachement"); }
    finally { setSaving(false); }
  };
  const detach = async () => {
    setSaving(true);
    try { await onSave({ update_form: true }); setOpen(false); toast.success("Formulaire retiré"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    setResetting(true);
    try { await onReset(); toast.success("Formulaire réinitialisé — le client peut le remplir à nouveau"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de la réinitialisation"); }
    finally { setResetting(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid={`form-add-${testId}`}
        className="mt-3 text-xs text-marine border border-marine/20 rounded-sm px-2.5 py-1.5 hover:bg-marine hover:text-white transition-colors">
        + Attacher un formulaire
      </button>
    );
  }

  if (hasForm) {
    const nQuestions = form.sections.reduce((n, s) => n + s.questions.length, 0);
    return (
      <div className="mt-3 border-t border-marine/10 pt-3" data-testid={`form-editor-${testId}`}>
        <div className="text-xs uppercase tracking-wider text-marine/80 mb-2">Formulaire</div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-marine">
            {form.sections.length} section(s) · {nQuestions} question(s)
            <span className={`ml-2 text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${form.status === "completed" ? "bg-bleu-50 text-bleu border-bleu/30" : INTENT.attente.cls}`}>
              {form.status === "completed" ? "Complété" : "En attente"}
            </span>
          </div>
          <div className="flex gap-2">
            {form.status === "completed" && (
              <button onClick={reset} disabled={resetting} data-testid={`form-reset-${testId}`}
                className="text-xs border border-bordeaux/30 text-bordeaux rounded-sm px-3 py-1.5 hover:bg-bordeaux hover:text-white transition-colors disabled:opacity-50">Réinitialiser</button>
            )}
            <button onClick={detach} disabled={saving} data-testid={`form-detach-${testId}`}
              className="text-xs text-rouge-texte border border-rouge/30 rounded-sm px-3 py-1.5 hover:bg-rouge hover:text-white transition-colors disabled:opacity-50">Retirer</button>
          </div>
        </div>
        {form.status === "completed" && form.answers && (
          <div className="mt-3 space-y-2" data-testid={`form-responses-${testId}`}>
            {form.sections.map((s) => (
              <div key={s.id}>
                <div className="text-xs font-semibold text-marine">{s.title}</div>
                {s.questions.map((q) => {
                  const ans = form.answers.find((a) => a.question_id === q.id);
                  const val = ans ? ans.value : "";
                  const display = Array.isArray(val) ? (val.join(", ") || "—") : (val || "—");
                  return <div key={q.id} className="text-xs text-marine/80"><span className="text-marine/80">{q.label} : </span>{display}</div>;
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const TypeBtn = ({ v, label }) => (
    <button type="button" onClick={() => setMode(v)} data-testid={`form-mode-${v}-${testId}`}
      className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${mode === v ? "bg-marine text-white border-marine" : "border-marine/25 text-marine hover:bg-marine/5"}`}>
      {label}
    </button>
  );

  return (
    <div className="mt-3 border-t border-marine/10 pt-3" data-testid={`form-editor-${testId}`}>
      <div className="text-xs uppercase tracking-wider text-marine/80 mb-2">Formulaire</div>
      <div className="flex gap-2 mb-3">
        <TypeBtn v="template" label="Depuis un modèle" />
        <TypeBtn v="custom" label="Personnalisé" />
      </div>
      {mode === "template" ? (
        <div className="flex items-center gap-2">
          <label htmlFor={`form-template-select-${testId}`} className="sr-only">Modèle de formulaire</label>
          <select id={`form-template-select-${testId}`} value={templateId} onChange={(e) => setTemplateId(e.target.value)}
            data-testid={`form-template-select-${testId}`} className="flex-1 text-xs border border-marine/20 rounded-sm p-1.5 bg-white">
            <option value="">— Choisir un modèle —</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button onClick={attachTemplate} disabled={saving} data-testid={`form-attach-${testId}`}
            className="text-xs bg-marine text-white rounded-sm px-3 py-1.5 hover:bg-marine-hover transition-colors disabled:opacity-50">Attacher</button>
        </div>
      ) : (
        <p className="text-xs text-marine/80">
          Pour un formulaire personnalisé propre à cette étape, créez-le d'abord comme{" "}
          <Link to="/admin/automations-formulaires/new" target="_blank" rel="noopener noreferrer" className="text-marine underline">
            modèle
          </Link>{" "}
          puis attachez-le ici — cela garde la même bibliothèque de sections/questions réordonnables pour l'édition.
        </p>
      )}
      <button onClick={() => setOpen(false)} className="mt-2 text-xs text-marine/80 hover:underline">Annuler</button>
    </div>
  );
};

export default FormEditor;
